<template>
  <div id="jci-india-scoring-table" class="professional-scoring-component">
    <!-- Professional JCI India Scoring Table -->
    <v-card class="jci-india-scoring-card mb-6" elevation="3">
      <!-- Loading State -->
      <div class="scoring-loading-section" v-if="LoadingFlag">
        <div class="loading-wrapper pa-6">
          <v-progress-circular
            indeterminate
            color="#1976d2"
            size="48"
            width="4"
          ></v-progress-circular>
          <p class="loading-text mt-4">Loading JCI India scoring data...</p>
        </div>
      </div>

      <!-- Content Section -->
      <template v-else>
        <v-card-title class="scoring-header">
          <v-icon size="24" color="#1976d2" class="mr-3">mdi-chart-line</v-icon>
          <div class="scoring-title-section">
            <span class="section-title">{{
              CardTitle != "" ? CardTitle : "JCI India Score"
            }}</span>
            <div class="scoring-subtitle mt-1">
              <v-chip small color="#4caf50" text-color="white">
                <v-icon left size="12">mdi-auto-fix</v-icon>
                {{
                  CardSubTitle != ""
                    ? CardSubTitle
                    : TotalActualPoints + " Points Auto Generated"
                }}
              </v-chip>
            </div>
          </div>
          <v-spacer></v-spacer>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon color="#ff9800" v-bind="attrs" v-on="on"
                >mdi-information</v-icon
              >
            </template>
            <span>Points auto-generated from JCI India Members Portal</span>
          </v-tooltip>
        </v-card-title>

        <!-- Backend Details Meta Bar -->
        <div
          class="scoring-meta-info pa-4"
          v-if="AwardTypeTitle || ZoneName || LomName"
          style="background: #f4f6f9; border-bottom: 1px solid #e3f2fd"
        >
          <v-row dense align="center">
            <v-col cols="12" sm="6" md="3" v-if="AwardTypeTitle">
              <div class="meta-item">
                <v-icon size="16" color="#1976d2" class="mr-1"
                  >mdi-trophy-variant</v-icon
                >
                <strong>Award:</strong>
                <span class="ml-1"
                  >{{ AwardTypeNumber ? AwardTypeNumber + " - " : ""
                  }}{{ AwardTypeTitle }}</span
                >
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="3" v-if="AwardCategoryName">
              <div class="meta-item">
                <v-icon size="16" color="#1976d2" class="mr-1"
                  >mdi-shape</v-icon
                >
                <strong>Category:</strong>
                <span class="ml-1">{{ AwardCategoryName }}</span>
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="3" v-if="ZoneName">
              <div class="meta-item">
                <v-icon size="16" color="#1976d2" class="mr-1"
                  >mdi-map-marker</v-icon
                >
                <strong>Zone:</strong>
                <span class="ml-1">{{ ZoneName }}</span>
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="3" v-if="LomName">
              <div class="meta-item">
                <v-icon size="16" color="#1976d2" class="mr-1"
                  >mdi-domain</v-icon
                >
                <strong>LO:</strong>
                <span class="ml-1">{{ LomName }}</span>
              </div>
            </v-col>
          </v-row>
        </div>

        <v-card-text class="pa-0">
          <!-- Professional Scoring Table -->
          <div class="scoring-table-container">
            <v-data-table
              :headers="scoringHeader"
              :items="scoringData"
              hide-default-footer
              disable-pagination
              disable-sort
              class="professional-scoring-table"
              :class="{ 'mobile-table': $vuetify.breakpoint.mobile }"
            >
              <!-- Serial Number Column -->
              <template v-slot:[`item.slNo`]="{ index }">
                <div class="serial-number">
                  {{ index + 1 }}
                </div>
              </template>

              <!-- Criteria Column -->
              <template v-slot:[`item.Criteria`]="{ item }">
                <div class="criteria-cell">
                  <div class="criteria-text">{{ item.Criteria }}</div>
                  <div class="criteria-note" v-if="item.Note">
                    <small class="text-muted">{{ item.Note }}</small>
                  </div>
                </div>
              </template>

              <!-- Actual Value Column -->
              <template v-slot:[`item.ActualValue`]="{ item }">
                <div class="actual-value-cell">
                  <v-chip
                    small
                    color="#e0f7fa"
                    text-color="#006064"
                    class="points-chip actual-value-chip"
                  >
                    <v-icon left size="12">mdi-text-box-check-outline</v-icon>
                    {{ item.ActualValue ? item.ActualValue : "N/A" }}
                  </v-chip>
                </div>
              </template>

              <!-- Max Points Column -->
              <template v-slot:[`item.MaxPoints`]="{ item }">
                <div class="points-cell">
                  <v-chip
                    small
                    color="#9e9e9e"
                    text-color="white"
                    class="points-chip max-points-chip"
                  >
                    <v-icon left size="12">mdi-target</v-icon>
                    {{ item.MaxPoints }}
                  </v-chip>
                </div>
              </template>

              <!-- Member Received Points Column -->
              <template v-slot:[`item.ReceivedPoints`]="{ item }">
                <div class="received-points-cell">
                  <div class="points-display">
                    <v-chip
                      small
                      :color="item.PointsColor"
                      text-color="white"
                      class="points-chip received-points-chip"
                    >
                      <v-icon left size="12">mdi-star</v-icon>
                      {{ item.ReceivedPoints }}
                    </v-chip>
                  </div>
                </div>
              </template>
            </v-data-table>
          </div>

          <!-- Professional Summary Footer -->
          <div class="scoring-summary">
            <v-divider></v-divider>
            <div class="summary-content pa-4">
              <v-row align="center">
                <v-col cols="12" md="6">
                  <div class="summary-info">
                    <h4 class="summary-title">
                      <v-icon color="#1976d2" class="mr-2"
                        >mdi-calculator</v-icon
                      >
                      Total Auto-Generated Points
                    </h4>
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="text-right">
                  <div class="total-points-section">
                    <v-chip
                      large
                      color="#1976d2"
                      text-color="white"
                      class="total-points-chip"
                    >
                      <v-icon left size="18">mdi-trophy</v-icon>
                      {{ TotalReceivedPoints }} Points
                    </v-chip>
                  </div>
                </v-col>
              </v-row>
            </div>
          </div>
        </v-card-text>
      </template>
    </v-card>

    <!-- Empty State -->
    <v-card
      v-if="!shouldShowTable && !LoadingFlag && AwardTypeId && MemberId"
      class="empty-scoring-state"
      elevation="3"
    >
      <v-card-text class="text-center pa-6">
        <v-icon size="64" color="#ccc">mdi-chart-line-variant</v-icon>
        <h3 class="empty-state-title mt-4">No Scoring Data Available</h3>
        <p class="empty-state-text">
          Scoring criteria will appear here once member data is loaded.
        </p>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";

export default {
  name: "JciIndiaScoringTable",
  mixins: [common],
  props: {
    AwardCategoryId: {
      required: true,
    },
    AwardTypeId: {
      required: true,
    },
    MemberId: {
      required: true,
    },
    LomId: {
      required: true,
    },
  },
  data() {
    return {
      LoadingFlag: false,

      CardTitle: "",
      CardSubTitle: "",
      scoringData: [],
      scoringHeader: [],
      TotalActualPoints: 0,
      TotalReceivedPoints: 0,
      AwardTypeTitle: "",
      AwardTypeNumber: "",
      AwardCategoryName: "",
      LomName: "",
      ZoneName: "",
    };
  },
  computed: {
    shouldShowTable() {
      return this.AwardTypeId && this.MemberId && this.scoringData.length > 0;
    },
  },
  watch: {
    MemberId: function () {
      console.log("MemberId watch called");
      this.resetScoringData();
      this.getMemberScoringData();
    },
  },
  methods: {
    resetScoringData() {
      console.log("resetScoringData called");
      this.scoringData = [];
      this.TotalActualPoints = 0;
      this.TotalReceivedPoints = 0;
      this.AwardTypeTitle = "";
      this.AwardTypeNumber = "";
      this.AwardCategoryName = "";
      this.LomName = "";
      this.ZoneName = "";
    },

    getMemberScoringData() {
      console.log("getMemberScoringData called");

      var AwardTypeId = this.AwardTypeId;
      console.log({ AwardTypeId });

      if (AwardTypeId > 0) {
        this.resetScoringData();
        this.LoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/award-type/jci-india-score";

        var upload = {
          UserInterface: 1,
          AwardCategory: this.AwardCategoryId,
          AwardType: this.AwardTypeId,
          Member: this.MemberId,
          Lom: this.LomId,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.LoadingFlag = false;

            flag = response.data.flag;
            output = response.data.output;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log("records=" + JSON.stringify(records));

            if (flag == 1) {
              thisIns.CardTitle = records.ScoreCardTitle;
              thisIns.CardSubTitle = records.ScoreCardSubTitle;
              thisIns.TotalActualPoints = records.TotalActualPoints;
              thisIns.TotalReceivedPoints = records.TotalReceivedPoints;
              thisIns.scoringData = records.ScoringData;
              thisIns.scoringHeader = records.ScoringHeader;
              thisIns.AwardTypeTitle = records.AwardTypeTitle || "";
              thisIns.AwardTypeNumber = records.AwardTypeNumber || "";
              thisIns.AwardCategoryName = records.AwardCategoryName || "";
              thisIns.LomName = records.LomName || "";
              thisIns.ZoneName = records.ZoneName || "";
            }
          })
          .catch(function (error) {
            thisIns.LoadingFlag = false;
            console.log("error=" + error);
          });
      } else {
        var message = "";
        console.log("error=" + message);
      }
    },
  },

  created() {
    console.log("JciIndiaScoringTable created with props:", {
      AwardTypeId: this.AwardTypeId,
      MemberId: this.MemberId,
    });
    this.getMemberScoringData();
  },
};
</script>

<style lang="scss">
.professional-scoring-component {
  // JCI India Scoring Card Styles
  .jci-india-scoring-card {
    border-radius: 16px !important;
    overflow: hidden;
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fc 100%);
    border: 1px solid #e3f2fd;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 12px 35px rgba(25, 118, 210, 0.15) !important;
    }

    // Scoring Header Styles
    .scoring-header {
      background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
      color: white;
      padding: 20px 24px !important;

      .scoring-title-section {
        flex-grow: 1;

        .section-title {
          font-size: 1.4rem;
          font-weight: 600;
          letter-spacing: 0.5px;
          margin-bottom: 4px;
        }

        .scoring-subtitle {
          .v-chip {
            font-weight: 500;
            letter-spacing: 0.3px;
          }
        }
      }
    }

    // Scoring Table Container
    .scoring-table-container {
      .professional-scoring-table {
        // Table Header Styles
        ::v-deep .v-data-table-header {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);

          th {
            font-weight: 600 !important;
            color: #1976d2 !important;
            border-bottom: 2px solid #e3f2fd !important;
            padding: 16px !important;

            &.sortable:hover {
              background: rgba(25, 118, 210, 0.05);
            }
          }
        }

        // Table Body Styles
        ::v-deep tbody {
          tr {
            transition: all 0.2s ease;
            border-bottom: 1px solid #f0f0f0;

            &:hover {
              background: rgba(25, 118, 210, 0.03) !important;
              transform: translateX(2px);
            }

            &:nth-child(even) {
              background-color: #fafbfc;
            }

            td {
              padding: 16px !important;
              border-bottom: 1px solid #f0f0f0 !important;
            }
          }
        }

        // Serial Number Column
        .serial-number {
          background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
          color: white;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 0.875rem;
          margin: 0 auto;
        }

        // Criteria Column
        .criteria-cell {
          .criteria-text {
            font-weight: 500;
            color: #2c3e50;
            line-height: 1.4;
            margin-bottom: 4px;
          }

          .criteria-note {
            font-style: italic;
            color: #6c757d;
            font-size: 0.8rem;
          }
        }

        // Points Cell
        .points-cell {
          text-align: center;

          .points-chip {
            font-weight: 600;
            letter-spacing: 0.3px;

            &.max-points-chip {
              background: linear-gradient(
                135deg,
                #9e9e9e 0%,
                #757575 100%
              ) !important;
            }
          }
        }

        // Received Points Cell
        .received-points-cell {
          text-align: center;

          .points-display {
            display: flex;
            flex-direction: column;
            align-items: center;

            .received-points-chip {
              font-weight: 600;
              letter-spacing: 0.3px;
              margin-bottom: 4px;
            }

            .points-percentage {
              font-size: 0.75rem;
              font-weight: 500;
            }
          }
        }

        // Mobile Table Styles
        &.mobile-table {
          ::v-deep .v-data-table__wrapper {
            overflow-x: auto;
          }

          .criteria-cell {
            max-width: 200px;

            .criteria-text {
              font-size: 0.875rem;
              line-height: 1.3;
            }
          }

          .points-chip {
            font-size: 0.75rem !important;
          }
        }
      }
    }

    // Scoring Summary Styles
    .scoring-summary {
      background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
      border-top: 1px solid #e0e0e0;

      .summary-content {
        .summary-info {
          .summary-title {
            font-size: 1.2rem;
            font-weight: 600;
            color: #2c3e50;
            margin-bottom: 8px;
          }

          .summary-note {
            font-size: 0.875rem;
            color: #6c757d;
            font-style: italic;
            margin: 0;
          }
        }

        .total-points-section {
          display: flex;
          flex-direction: column;
          align-items: flex-end;

          .total-points-chip {
            font-size: 1.1rem !important;
            font-weight: 700;
            letter-spacing: 0.5px;
            padding: 12px 20px;
            height: auto !important;
          }

          .total-percentage {
            .percentage-chip {
              font-size: 0.8rem;
              font-weight: 600;
              letter-spacing: 0.3px;
            }
          }
        }
      }
    }
  }

  // Loading State
  .scoring-loading-section {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;

    .loading-wrapper {
      text-align: center;

      .loading-text {
        font-size: 1.1rem;
        color: #666;
        font-weight: 500;
        margin: 0;
      }
    }
  }

  // Empty State
  .empty-scoring-state {
    border-radius: 16px !important;
    border: 2px dashed #e0e0e0;
    background: #fafbfc;

    .empty-state-title {
      font-size: 1.3rem;
      font-weight: 600;
      color: #666;
      margin: 0;
    }

    .empty-state-text {
      font-size: 1rem;
      color: #999;
      margin: 8px 0 0 0;
    }
  }

  // Responsive Design
  @media (max-width: 960px) {
    .jci-india-scoring-card {
      .scoring-header {
        padding: 16px !important;

        .scoring-title-section {
          .section-title {
            font-size: 1.2rem;
          }
        }
      }

      .summary-content {
        .total-points-section {
          align-items: center !important;
          margin-top: 1rem;

          .total-points-chip {
            font-size: 1rem !important;
          }
        }
      }
    }
  }

  @media (max-width: 600px) {
    .jci-india-scoring-card {
      margin: 0 -8px;
      border-radius: 8px !important;

      .scoring-header {
        padding: 12px 16px !important;
        flex-direction: column;
        align-items: flex-start;

        .scoring-title-section {
          width: 100%;
          margin-bottom: 12px;

          .section-title {
            font-size: 1.1rem;
          }
        }
      }

      .scoring-table-container {
        .professional-scoring-table {
          ::v-deep tbody tr td {
            padding: 12px 8px !important;
          }
        }
      }

      .summary-content {
        padding: 16px !important;

        .summary-info {
          text-align: center;
          margin-bottom: 16px;

          .summary-title {
            font-size: 1.1rem;
          }
        }

        .total-points-section {
          align-items: center !important;
        }
      }
    }
  }
}

// Dark Theme Support
.theme--dark {
  .professional-scoring-component {
    .jci-india-scoring-card {
      background: linear-gradient(135deg, #1e1e1e 0%, #2d2d2d 100%);
      border-color: #424242;

      .scoring-table-container {
        .professional-scoring-table {
          ::v-deep .v-data-table-header th {
            background: #2d2d2d !important;
            color: #90caf9 !important;
            border-bottom-color: #424242 !important;
          }

          ::v-deep tbody tr {
            &:nth-child(even) {
              background-color: #2d2d2d;
            }

            &:hover {
              background: rgba(144, 202, 249, 0.1) !important;
            }

            td {
              border-bottom-color: #424242 !important;
            }
          }
        }
      }

      .scoring-summary {
        background: linear-gradient(135deg, #2d2d2d 0%, #1e1e1e 100%);
        border-top-color: #424242;

        .summary-info {
          .summary-title {
            color: #e0e0e0;
          }

          .summary-note {
            color: #bdbdbd;
          }
        }
      }
    }

    .empty-scoring-state {
      background: #2d2d2d;
      border-color: #424242;

      .empty-state-title {
        color: #bdbdbd;
      }

      .empty-state-text {
        color: #757575;
      }
    }
  }
}

// Print Styles
@media print {
  .professional-scoring-component {
    .jci-india-scoring-card {
      box-shadow: none !important;
      border: 1px solid #ccc;

      .scoring-header {
        background: #f5f5f5 !important;
        color: #333 !important;
      }

      .scoring-summary {
        background: #f9f9f9 !important;
      }
    }
  }
}
</style>
